import React from 'react'
import { Box, Typography } from '@mui/material'
import { ColorDot } from '@/design-system'
import { MAT_COLORS } from '../../catalog'
import { FieldLabel, StepLead } from '../StepCopy'

export interface MattingControlsProps {
  width: number
  color: string
  onWidthChange: (w: number) => void
  onColorChange: (hex: string) => void
}

const WIDTH_PRESETS = [
  { label: 'None', value: 0 },
  { label: 'Thin', value: 0.05 },
  { label: 'Medium', value: 0.1 },
  { label: 'Wide', value: 0.18 },
] as const

export function MattingControls({
  width,
  color,
  onWidthChange,
  onColorChange,
}: MattingControlsProps) {
  const photoPct = Math.max(40, 100 - width * 200)

  return (
    <Box>
      <StepLead>Mat sits between the frame and the photo. Best with a frame selected.</StepLead>

      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={5}
        alignItems={{ sm: 'flex-start' }}
      >
        {/* Controls */}
        <Box flex={1} minWidth={0} maxWidth={420}>
          <FieldLabel>Width</FieldLabel>
          <Box display='flex' flexWrap='wrap' gap={1} mb={2}>
            {WIDTH_PRESETS.map((p) => {
              const active = Math.abs(width - p.value) < 0.015
              return (
                <Box
                  key={p.label}
                  component='button'
                  type='button'
                  onClick={() => onWidthChange(p.value)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    px: 1.75,
                    py: 0.85,
                    borderRadius: 'var(--ds-radius-pill)',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    background: active ? 'var(--ds-inverse)' : 'var(--ds-hover)',
                    color: active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink)',
                    transition: 'background var(--ds-duration-fast) var(--ds-ease)',
                    '&:focus-visible': {
                      outline: '2px solid var(--ds-ink)',
                      outlineOffset: 2,
                    },
                  }}
                >
                  {p.label}
                </Box>
              )
            })}
          </Box>

          <Box display='flex' alignItems='center' gap={2} mb={4}>
            <Box
              component='input'
              type='range'
              min={0}
              max={0.25}
              step={0.01}
              value={width}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onWidthChange(Number(e.target.value))
              }
              aria-label='Mat width'
              sx={{
                flex: 1,
                accentColor: 'var(--ds-ink)',
                height: 4,
                cursor: 'pointer',
              }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--ds-ink-muted)',
                minWidth: 40,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Math.round(width * 100)}%
            </Typography>
          </Box>

          <FieldLabel>Color</FieldLabel>
          <Box display='flex' flexWrap='wrap' gap={2} mt={0.5}>
            {MAT_COLORS.map((c) => (
              <Box key={c.id} display='flex' flexDirection='column' alignItems='center' gap={1}>
                <ColorDot
                  color={c.hex}
                  size={32}
                  selected={color === c.hex}
                  label={c.name}
                  onClick={() => onColorChange(c.hex)}
                />
                <Typography
                  sx={{
                    fontSize: 10,
                    color: color === c.hex ? 'var(--ds-ink)' : 'var(--ds-ink-subtle)',
                    fontWeight: color === c.hex ? 600 : 500,
                  }}
                >
                  {c.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Live preview — framed print */}
        <Box
          flexShrink={0}
          mx={{ xs: 'auto', sm: 0 }}
          width={200}
          height={200}
          borderRadius={4}
          display='flex'
          alignItems='center'
          justifyContent='center'
          aria-hidden
          sx={{
            background: color,
            boxShadow: 'var(--ds-shadow-soft)',
            outline: '10px solid #1a1a1a',
            outlineOffset: 0,
          }}
        >
          <Box
            width={`${photoPct}%`}
            height={`${photoPct}%`}
            minWidth='36%'
            minHeight='36%'
            borderRadius={1}
            sx={{
              background:
                'linear-gradient(145deg, var(--ds-hover) 0%, var(--ds-border) 100%)',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
